import { Event } from "../types/Event";
import { PaginatedResponse } from "../types/PaginatedResponse";
import { api } from "./api/apiSlice";

export interface GetEventParams {
  page?: number;
  size?: number;
  search?: string;
  filterBy?: string;
}

export const eventApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query<PaginatedResponse<Event>, GetEventParams>({
      query: (params) => ({
        url: "events/",
        params: {
          page: params?.page || 1,
          size: params?.size || 10,
          filterBy: params?.filterBy,
          ...(params?.search && { search: params.search }),
        },
      }),
      providesTags: ["events"],
    }),
    // update eventApi.ts
    updateEvent: builder.mutation<Event, any>({
      query: (data) => ({
        url: `events/${data?.id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["events"],
    }),
    publishEvent: builder.mutation<Event, any>({
      query: (data) => ({
        url: `events/publish/${data?.id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["events"],
    }),
    addEvent: builder.mutation<Event, any>({
      query: (data) => ({
        url: `events/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["events"],
    }),
    getEventById: builder.query<Event, number | string>({
      query: (id) => `events/${id}`,
    }),
    // in your eventApi.ts
    registerEvent: builder.mutation<any, any>({
      query: (body) => ({
        url: `event-registrations/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["upcomingEvents", "registeredEvents"],
    }),
    getRegisteredEventById: builder.query<
      any,
      { eventId: number | string | undefined; page: number; limit: number }
    >({
      query: ({ eventId, page, limit }) =>
        `events/registrations/event/${eventId}?page=${page}&page_size=${limit}`,
    }),
    getRegisteredEventByMemberId: builder.query<any, any>({
      query: (member_id) => `events/registrations-by-member/${member_id}`,
      providesTags: ["registeredEvents"],
    }),
    getRegistrationById: builder.query<any, any>({
      query: (id) => `events/registrations/${id}`,
    }),
    // Get event details with registrations by event ID
    getEventDetailsById: builder.query<any, string>({
      query: (eventId) => `events/registrations/single/${eventId}`,
      providesTags: ["events"],
    }),
    getEventRegistration: builder.query<any, string>({
      query: (eventId) => `event-registrations/${eventId}`,
      providesTags: ["events"],
    }),
    // New endpoints for upcoming and registered events
    getUpcomingEventsByMember: builder.query<
      Event[],
      { memberId: string; limit?: number }
    >({
      query: ({ memberId, limit = 10 }) =>
        `events/upcoming/member/${memberId}?limit=${limit}`,
      providesTags: ["upcomingEvents"],
    }),
    getUpcomingEvents: builder.query<Event[], { limit?: number }>({
      query: ({ limit = 10 }) => `events/upcoming?limit=${limit}`,
      providesTags: ["events"],
    }),
    getPastEvents: builder.query<Event[], { limit?: number }>({
      query: ({ limit = 10 }) => `events/past?limit=${limit}`,
      providesTags: ["events"],
    }),

    // My Registrations API endpoints
    getMyRegisteredEvents: builder.query<
      {
        registrations: Array<{
          id: string;
          fullName: string;
          phoneNumber: string;
          emailAddress?: string;
          nidNumber?: string;
          address: string;
          isIncludeDriver: boolean;
          createdAt: string;
          updatedAt: string;
          event: {
            id: string;
            eventName: string;
            description?: string;
            eventDate?: string;
            eventTime?: string;
            eventLocation?: string;
            memberFee?: number;
            familyMemberFee?: number;
            guestFee?: number;
            driverFee?: number;
            createdAt: string;
          };
          familyMembersCount: number;
          guestsCount: number;
          hasDriver: boolean;
        }>;
        totalCount: number;
        page: number;
        perPage: number;
        totalPages: number;
      },
      { user_email: string; page?: number; per_page?: number }
    >({
      query: ({ user_email, page = 1, per_page = 10 }) =>
        `event-registrations/my-registrations?user_email=${user_email}&page=${page}&per_page=${per_page}`,
      providesTags: ["events"],
    }),

    getMyRegistrationsByMemberId: builder.query<
      {
        registrations: Array<{
          id: string;
          fullName: string;
          phoneNumber: string;
          emailAddress?: string;
          nidNumber?: string;
          address: string;
          isIncludeDriver: boolean;
          createdAt: string;
          updatedAt: string;
          event: {
            id: string;
            eventName: string;
            description?: string;
            eventDate?: string;
            eventTime?: string;
            eventLocation?: string;
            memberFee?: number;
            familyMemberFee?: number;
            guestFee?: number;
            driverFee?: number;
            createdAt: string;
          };
          familyMembersCount: number;
          guestsCount: number;
          hasDriver: boolean;
          hasPaid: boolean;
          paymentStatus?: string;
        }>;
        totalCount: number;
        page: number;
        perPage: number;
        totalPages: number;
      },
      { member_id: string; page?: number; per_page?: number }
    >({
      query: ({ member_id, page = 1, per_page = 10 }) =>
        `event-registrations/my-registrations/member/${member_id}?page=${page}&per_page=${per_page}`,
      providesTags: ["events"],
    }),

    getMyRegistrationsSummary: builder.query<
      {
        totalRegistrations: number;
        upcomingEvents: number;
        pastEvents: number;
        totalFamilyMembers: number;
        totalGuests: number;
        registrationsWithDriver: number;
      },
      { user_email: string }
    >({
      query: ({ user_email }) =>
        `event-registrations/my-registrations/summary?user_email=${user_email}`,
      providesTags: ["events"],
    }),

    getRegistrationDetails: builder.query<
      {
        id: string;
        fullName: string;
        phoneNumber: string;
        emailAddress?: string;
        nidNumber?: string;
        address: string;
        isIncludeDriver: boolean;
        createdAt: string;
        updatedAt: string;
        event: {
          id: string;
          eventName: string;
          description?: string;
          eventDate?: string;
          eventTime?: string;
          eventLocation?: string;
          memberFee?: number;
          familyMemberFee?: number;
          guestFee?: number;
          driverFee?: number;
          createdAt: string;
        };
        familyMembersCount: number;
        guestsCount: number;
        hasDriver: boolean;
        familyMembers?: Array<{
          fullName: string;
          relationship: string;
          age?: string;
          nidNumber?: string;
          address: string;
        }>;
        guests?: Array<{
          fullName: string;
          relationshipToMember: string;
          contractNumber: string;
          nidNumber?: string;
          address: string;
        }>;
        driverInfo?: {
          fullName: string;
          phoneNumber: string;
          vehicleNumber: string;
          licenseNumber: string;
        };
      },
      { registration_id: string; include_details?: boolean }
    >({
      query: ({ registration_id, include_details = false }) =>
        `event-registrations/${registration_id}?include_details=${include_details}`,
      providesTags: ["events"],
    }),
    // PDF Download endpoints
    updateRegistration: builder.mutation<any, any>({
      query: ({ registrationId, ...body }) => ({
        url: `event-registrations/${registrationId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["events"],
    }),
    downloadVoucherPdf: builder.mutation<Blob, string>({
      query: (eventId) => ({
        url: `events/voucher/download/${eventId}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),
    downloadTicketPdf: builder.mutation<Blob, string>({
      query: (eventId) => ({
        url: `events/ticket/download/${eventId}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }), // Payment Statistics endpoint
    getEventPaymentStatistics: builder.query<
      {
        event_id: string;
        event_name: string;
        total_participants: number;
        total_amount: number;
        total_paid: number;
        total_due: number;
        paid_count: number;
        due_count: number;
      },
      string
    >({
      query: (eventId) => `events/payment-statistics/${eventId}`,
      providesTags: (result, error, eventId) => [
        { type: "events", id: `payment-stats-${eventId}` },
      ],
    }),
  }),
});

export const {
  useUpdateEventMutation,
  useGetEventsQuery,
  useGetEventByIdQuery,
  useRegisterEventMutation,
  useGetRegisteredEventByIdQuery,
  useGetRegisteredEventByMemberIdQuery,
  useGetRegistrationByIdQuery,
  useGetEventDetailsByIdQuery,
  useAddEventMutation,
  useGetUpcomingEventsQuery,
  useGetPastEventsQuery,
  useGetMyRegisteredEventsQuery,
  useGetMyRegistrationsByMemberIdQuery,
  useGetMyRegistrationsSummaryQuery,
  useGetRegistrationDetailsQuery,
  useDownloadVoucherPdfMutation,
  useDownloadTicketPdfMutation,
  usePublishEventMutation,
  useGetUpcomingEventsByMemberQuery,
  useGetEventRegistrationQuery,
  useGetEventPaymentStatisticsQuery,
  useUpdateRegistrationMutation,
} = eventApi;
